/* import __COLOCATED_TEMPLATE__ from './campaign-banner.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type ModalService from 'embercom/services/modal-service';

type BannerVariant = 'paywall' | 'success';

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  paywall: 'border-paywall-border bg-paywall-container',
  success: 'border-success-border bg-success-container',
};

interface Signature {
  Element: HTMLDivElement;
  Args: {
    bannerText: string;
    bannerCtaText?: string;
    onCtaClick?: () => void;
    variant?: BannerVariant;
    showTalkToSpecialist?: boolean;
    syntheticsSelector?: string;
    trialPlanName?: string;
    daysLeftOfTrial?: number;
    validEarlyStageApplicant?: boolean;
    canUseEarlyStageDetection?: boolean;
    canUseProductSpecialistCTAs?: boolean;
    canUseTalkToSalesCTAs?: boolean;
    canSwitchTrialPlan?: boolean;
  };
}

export default class CampaignBanner extends Component<Signature> {
  @service declare purchaseAnalyticsService: $TSFixMe;
  @service declare modalService: ModalService;
  @tracked isPlanTrialModalOpen = false;

  get variantClasses(): string {
    return VARIANT_CLASSES[this.args.variant ?? 'paywall'];
  }

  get showTalkToSpecialist(): boolean {
    return !!(
      (this.args.canUseProductSpecialistCTAs || this.args.canUseTalkToSalesCTAs) &&
      !this.args.validEarlyStageApplicant &&
      this.args.showTalkToSpecialist !== false
    );
  }

  get talkToSpecialistBotTarget(): string {
    return this.args.canUseProductSpecialistCTAs
      ? 'product-specialist-trial-banner'
      : 'talk-to-sales-trial-banner';
  }

  get talkToSpecialistCtaText(): string {
    return this.args.canUseProductSpecialistCTAs ? 'ask-product-specialist' : 'talk-to-sales';
  }

  get talkToSpecialistAnalyticsObject(): string {
    return this.args.canUseProductSpecialistCTAs ? 'ask_product_specialist' : 'talk_to_sales';
  }

  @action
  onProductSpecialistClick() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      object: this.talkToSpecialistAnalyticsObject,
      context: 'campaign_banner',
    });
  }

  @action
  handlePlanNameClick(event: Event) {
    if ((event.target as HTMLElement)?.classList.contains('clickable-plan-name')) {
      this.purchaseAnalyticsService.trackEvent({
        action: 'clicked',
        object: 'plan_name',
        context: 'campaign_banner',
      });

      if (this.args.canSwitchTrialPlan) {
        this.modalService.openModal('react/plan-switch-modal', {
          source: 'banner',
          onClose: () => this.modalService.closeModal(),
        });
      } else {
        this.isPlanTrialModalOpen = true;
      }
    }
  }

  @action
  closePlanTrialModal() {
    this.isPlanTrialModalOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Banners::CampaignBanner': typeof CampaignBanner;
    'banners/campaign-banner': typeof CampaignBanner;
  }
}
